import { useState, useEffect } from 'react';
import { Theme, Flex, Text, Heading, Table, Spinner, Badge } from '@radix-ui/themes';
import { LockKeyhole, Globe, LogOut } from 'lucide-react';
import { apiFetch } from './api';
import InvoiceDialog from './InvoiceDialog';

import { formatUGX } from "./UtilityFunctions";
/**
 * What a dealership sees when their account is suspended for non-payment.
 *
 * Before this existed the suspension gate in app.js returned 402 to every
 * request and nothing on the client knew what that meant, so a suspended tenant
 * saw an app that appeared simply broken — empty sheets, failed saves, no
 * explanation anywhere except Settings → Invoices & Credits, which they had no reason to
 * open. Someone who cannot tell "we cut you off" from "your software is broken"
 * calls support instead of paying, which defeats the point of automating
 * collection at all.
 *
 * Three things it has to do:
 *
 *  - Say plainly what is owed and what happens when it is settled. The headline
 *    comes from the SERVER's own message rather than being hardcoded here, so
 *    changing the policy (suspend vs. drop to a free tier, say) changes the copy
 *    in one place and does not need a frontend deploy.
 *  - Let them get the invoice. /billing is deliberately reachable while
 *    suspended, so the document is downloadable from right here — asking someone
 *    to pay a bill you will not show them is not a serious request.
 *  - Reassure them their public website is untouched. It serves their customers,
 *    who owe us nothing, and a dealership that thinks their storefront is down
 *    will panic about the wrong thing.
 */

//Shared formatter — see formatUGX in UtilityFunctions.jsx. This rendered
//"1,500,000 UGX" (suffixed) while most of the app rendered "UGX 1,500,000".
const ugx = (n) => formatUGX(n || 0);
const day = (iso) => (iso
  ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  : '—');

export default function AccountSuspended({ message, onSignOut }) {
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiFetch('/billing/credits', { method: 'GET' }).then((r) => r.json()).catch(() => null),
      apiFetch('/billing/invoices', { method: 'GET' }).then((r) => r.json()).catch(() => null),
    ]).then(([credits, list]) => {
      if (cancelled) return;
      if (credits?.success) setData(credits);
      // Unpaid first: the ones that need settling are the point of this screen.
      const rows = (list?.invoices || []).filter((i) => i.Status !== 'written_off');
      setInvoices(rows);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const owed = data?.outstandingUGX;

  return (
    <Theme accentColor="indigo" radius="medium" scaling="100%">
      <div style={{
        minHeight: '100vh', background: 'var(--theme-surface-alt)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '32px 16px',
      }}>
        <div style={{
          width: '100%', maxWidth: 640, background: 'var(--theme-surface)',
          border: '1px solid var(--theme-line)', borderRadius: 'var(--theme-radius-lg)',
          boxShadow: 'var(--theme-shadow-dialog)', overflow: 'hidden',
        }}>
          <div style={{
            background: 'var(--theme-danger-tint)', color: 'var(--theme-danger-ink)',
            padding: '18px 20px', borderBottom: '1px solid var(--theme-line)',
          }}>
            <Flex align="center" gap="3">
              <LockKeyhole size={22} style={{ flexShrink: 0 }} />
              <div>
                <Heading size="4" style={{ color: 'inherit', margin: 0 }}>
                  Account suspended
                </Heading>
                {data?.companyName && (
                  <Text size="2" style={{ opacity: 0.85 }}>{data.companyName}</Text>
                )}
              </div>
            </Flex>
          </div>

          <div style={{ padding: 20 }}>
            {/* The server's wording, not ours — see the note at the top. */}
            <Text size="3" style={{ display: 'block', marginBottom: 16 }}>
              {message || 'This account is suspended for non-payment. Settle the outstanding invoice to restore access.'}
            </Text>

            {loading ? (
              <Flex align="center" gap="2" py="3">
                <Spinner /><Text size="2" style={{ color: 'var(--theme-muted)' }}>Loading your invoices…</Text>
              </Flex>
            ) : (
              <>
                {owed > 0 && (
                  <div style={{
                    background: 'var(--theme-surface-alt)', border: '1px solid var(--theme-line)',
                    borderRadius: 'var(--theme-radius-sm)', padding: '12px 14px', marginBottom: 16,
                  }}>
                    <Text size="2" style={{ color: 'var(--theme-muted)', display: 'block' }}>
                      Outstanding
                    </Text>
                    <Text size="6" weight="bold">{ugx(owed)}</Text>
                    {data?.daysOverdue > 0 && (
                      <Text size="2" style={{ color: 'var(--theme-muted)', display: 'block' }}>
                        {data.daysOverdue} days overdue
                      </Text>
                    )}
                  </div>
                )}

                {invoices.length > 0 && (
                  <>
                    <div className="section-label-t">Your invoices</div>
                    <Table.Root size="1" mb="3">
                      <Table.Body>
                        {invoices.map((i) => (
                          <Table.Row key={i.ID} onClick={() => setOpenId(i.ID)}
                                     style={{ cursor: 'pointer' }}>
                            <Table.Cell>{i.InvoiceNo || i.Period}</Table.Cell>
                            <Table.Cell>
                              <Text size="1" style={{ color: 'var(--theme-muted)' }}>
                                due {day(i.DueAt)}
                              </Text>
                            </Table.Cell>
                            <Table.Cell align="right">{ugx(i.TotalUGX)}</Table.Cell>
                            <Table.Cell align="right">
                              <Badge color={i.Status === 'paid' ? 'green' : 'amber'}>
                                {i.Status === 'paid' ? 'Paid' : 'Due'}
                              </Badge>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                    <Text size="1" style={{ color: 'var(--theme-muted)', display: 'block', marginBottom: 16 }}>
                      Open an invoice to see what it covers or download a PDF copy.
                    </Text>
                  </>
                )}

                {/* Payment is verified by hand, so promising instant restoration
                    would be a lie. Say what actually happens. */}
                <Text size="2" style={{ color: 'var(--theme-muted)', display: 'block', marginBottom: 14 }}>
                  Access is restored as soon as we confirm payment. Payments are checked
                  manually, so let us know once you have sent it and we will clear it
                  the same day.
                </Text>

                <div style={{
                  background: 'var(--theme-accent-tint)', color: 'var(--theme-accent-ink)',
                  borderRadius: 'var(--theme-radius-sm)', padding: '10px 12px',
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                }}>
                  <Globe size={17} style={{ flexShrink: 0, marginTop: 1 }} />
                  <Text size="2">
                    Your public website is unaffected and is still online for your customers.
                  </Text>
                </div>
              </>
            )}
          </div>

          <Flex justify="end" p="3" style={{
            background: 'var(--theme-surface-alt)', borderTop: '1px solid var(--theme-line)',
          }}>
            <button className="btn-t btn-t-secondary" onClick={onSignOut}>
              <LogOut size={15} /> Sign out
            </button>
          </Flex>
        </div>

        {openId && (
          <InvoiceDialog
            invoiceId={openId}
            companyName={data?.companyName}
            onClose={() => setOpenId(null)}
          />
        )}
      </div>
    </Theme>
  );
}
